import { LinkedTG } from '@/api/Helpers'
import { i18n } from '@/app/I18n'
import { currentMembership } from '@/state/Membership'

export function isForFlexibleTickets(group: TicketGroup): boolean {
  return group.handler === 'passes' || group.handler === 'codes'
}

export function isForCodes(group: TicketGroup): boolean {
  return group.handler === 'codes'
}

export function isForMembersOnly(entity: EventTemplate | TicketGroup): boolean {
  return entity.hidden_type === 'public_member_only'
}

export function hasSeated(groups: LinkedTG[]) {
  return groups.some(({ handler }) => handler === 'seated') || false
}

export type TGQueryParam = undefined | string | string[]

function getStepperTitle(key: string, groups: TicketGroup[], giftOfMembership: boolean): string {
  if (giftOfMembership) {
    return i18n.t('stepperGroupTitles.giftOfMembershipTitle') as string
  }

  const isFlexibleTickets = groups.every(isForFlexibleTickets)
  const type = isFlexibleTickets ? 'passes' : 'tickets'
  return i18n.t(`stepperGroupTitles.${key}`, { type }) as string
}

function getStepperDescription(giftOfMembership: boolean) {
  if (giftOfMembership) {
    return i18n.t('stepperGroupTitles.giftOfMembershipDescription') as string
  }
  return null
}

export function getMemberAndGuestStepperGroups(groups: LinkedTG[], giftOfMembership: boolean, limits?: MemberBenefits) {
  const result: Array<{
    title: string | null
    description: string | null
    className: string
    groups: LinkedTG[]
  }> = []

  const { memberTGs, guestTGs } = getMemberAndGuestTicketGroups(groups, limits)
  const description = getStepperDescription(giftOfMembership)

  if (memberTGs.length > 0) {
    const title = getStepperTitle('members', memberTGs, giftOfMembership)
    result.push({
      title,
      description,
      groups: memberTGs,
      className: 'member-ticket-steppers',
    })
  }

  if (guestTGs.length > 0) {
    const languageKey = memberTGs.length > 0 ? 'guests' : 'default'
    const title = getStepperTitle(languageKey, guestTGs, giftOfMembership)
    result.push({
      title,
      description,
      groups: guestTGs,
      className: 'guest-ticket-steppers',
    })
  }

  return result
}

function getMemberAndGuestTicketGroups(
  groups: LinkedTG[],
  limits?: MemberBenefits,
): {
  memberTGs: LinkedTG[]
  guestTGs: LinkedTG[]
} {
  const memberTGs: LinkedTG[] = []
  const guestTGs: LinkedTG[] = []

  for (const ticketGroup of groups) {
    const isMembersTicketGroup = isForMembersOnly(ticketGroup)
    if (isMembersTicketGroup && limits) {
      // Filter out member-only ticket types with no purchase limits.
      const types = ticketGroup.types.filter((type) => limits[type.name] ?? limits['*'])
      memberTGs.push({ ...ticketGroup, types })
    } else if (!isMembersTicketGroup) {
      guestTGs.push(ticketGroup)
    }
  }

  return { memberTGs, guestTGs }
}

export function availableTicketGroups(ticketGroups) {
  const groups = ticketGroups.filter((group) => group.types.length > 0)

  if (currentMembership()) {
    return groups
  } else {
    return groups.filter((group) => group.hidden_type !== 'public_member_only')
  }
}
